import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Mail as MailIcon, Star as StarIcon } from '@mui/icons-material';

type PendingInvite = {
  token: string;
  novelTitle: string;
  role: string;
  ownerId: string;
};

type CollaborationInviteDialogProps = {
  open: boolean;
  invite: PendingInvite | null;
  onClose: () => void;
  onAccept: (token: string) => Promise<void>;
  onDecline: (token: string) => Promise<void>;
  isProcessing: boolean;
};

export function CollaborationInviteDialog({
  open,
  invite,
  onClose,
  onAccept,
  onDecline,
  isProcessing,
}: CollaborationInviteDialogProps) {
  const { t } = useTranslation();

  if (!invite) return null;

  return (
    <Dialog
      open={open}
      onClose={isProcessing ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, fontWeight: 700, pb: 1 }}>
        <MailIcon sx={{ color: 'primary.main' }} />
        {t('dashboard.invitations.dialog_title')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <Box
            sx={{
              p: 2,
              borderRadius: 2.5,
              bgcolor: 'action.hover',
              border: '1px solid',
              borderColor: 'divider',
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('dashboard.invitations.invited_by')}
            </Typography>
            <Typography variant="h3" sx={{ fontSize: '1.25rem', fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
              "{invite.novelTitle}"
            </Typography>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.5,
                py: 0.5,
                borderRadius: 1.5,
                bgcolor: 'rgba(161, 130, 74, 0.15)',
                color: 'primary.main',
                fontSize: '0.75rem',
                fontWeight: 700,
                mt: 1,
              }}
            >
              <StarIcon sx={{ fontSize: '0.9rem' }} />
              {invite.role.toUpperCase()}
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary" align="center" display="block">
            {t('dashboard.invitations.auto_select_hint')}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 1, gap: 1 }}>
        <Button
          onClick={() => onDecline(invite.token)}
          disabled={isProcessing}
          variant="outlined"
          color="error"
          sx={{ flex: 1, borderRadius: 1.5 }}
        >
          {isProcessing ? <CircularProgress size={20} color="inherit" /> : t('dashboard.invitations.decline')}
        </Button>
        <Button
          onClick={() => onAccept(invite.token)}
          disabled={isProcessing}
          variant="contained"
          color="primary"
          sx={{ flex: 1, borderRadius: 1.5 }}
        >
          {isProcessing ? <CircularProgress size={20} color="inherit" /> : t('dashboard.invitations.accept')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
